'use client'

import { useStore } from 'jotai'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { PRIORITY_TICKERS } from '@/features/chart/lib/configs/options-config'
import { resolveChainUnderlyingPrice } from '@/features/chart/lib/option-chain-underlying'
import {
  buildOptionSymbolMap,
  filterAndGroupInstruments,
  getExpiryTimestamp,
  getNearesetExpiry,
  getStrikesForExpiry,
  isExpiryStillAvailable,
  resolveOptionSymbol,
} from '@/features/chart/lib/utils/option-util'
import {
  useLinkingGroupOptionRows,
  useLinkingGroupOptionUnderlyingPrice,
  useLinkingGroupState,
} from '@/hooks/use-linking-group-state'
import { useOrderFormDispatch } from '@/hooks/use-order-form-dispatch'
import { useOrderFormSelectionTarget } from '@/hooks/use-order-form-selection-target'
import { safeParseFloat } from '@/lib/utils'
import {
  optionIncrementalTickerAtomFamily,
  registerOptionTickerSymbol,
  unregisterOptionTickerSymbol,
} from '@/state/market-data-cache'
import {
  Exchange,
  IncrementalTicker,
  Instrument,
  InstrumentKind,
} from '@/types/generated/common/types'

/**
 * Merge only non-undefined fields from incoming ticker into existing ticker
 * Used for UPDATE_TYPE_CHANGE to only update changed fields
 */
function mergeTickerUpdate(
  existing: IncrementalTicker | undefined,
  incoming: IncrementalTicker
): IncrementalTicker {
  if (!existing) return incoming

  return {
    // Always update identity fields
    symbol: incoming.symbol,
    timestamp: incoming.timestamp,
    type: incoming.type,
    exchange: incoming.exchange,
    // Merge optional fields - only update if defined in incoming
    state: incoming.state ?? existing.state,
    lastPrice: incoming.lastPrice ?? existing.lastPrice,
    bestBidPrice: incoming.bestBidPrice ?? existing.bestBidPrice,
    bestAskPrice: incoming.bestAskPrice ?? existing.bestAskPrice,
    markPrice: incoming.markPrice ?? existing.markPrice,
    indexPrice: incoming.indexPrice ?? existing.indexPrice,
    settlementPrice: incoming.settlementPrice ?? existing.settlementPrice,
    bestBidQuantity: incoming.bestBidQuantity ?? existing.bestBidQuantity,
    bestAskQuantity: incoming.bestAskQuantity ?? existing.bestAskQuantity,
    openInterest: incoming.openInterest ?? existing.openInterest,
    markIv: incoming.markIv ?? existing.markIv,
    bidIv: incoming.bidIv ?? existing.bidIv,
    askIv: incoming.askIv ?? existing.askIv,
    underlyingPrice: incoming.underlyingPrice ?? existing.underlyingPrice,
    underlyingIndex: incoming.underlyingIndex ?? existing.underlyingIndex,
    interestRate: incoming.interestRate ?? existing.interestRate,
    // greeks/stats are sub-objects that can themselves arrive partial (e.g. a message
    // carrying only `vega`) — merge field-by-field so an update to one greek doesn't drop
    // the others back to undefined (renders as 0 in option-row.tsx).
    // Note: ts-proto's decode() always sets every field key (to undefined if absent from
    // the wire message), so a plain `{...existing, ...incoming}` spread would overwrite
    // untouched fields with `undefined` anyway — each field must be merged with `??`.
    greeks:
      incoming.greeks || existing.greeks
        ? {
            delta: incoming.greeks?.delta ?? existing.greeks?.delta,
            gamma: incoming.greeks?.gamma ?? existing.greeks?.gamma,
            theta: incoming.greeks?.theta ?? existing.greeks?.theta,
            vega: incoming.greeks?.vega ?? existing.greeks?.vega,
            rho: incoming.greeks?.rho ?? existing.greeks?.rho,
          }
        : undefined,
    stats:
      incoming.stats || existing.stats
        ? {
            high: incoming.stats?.high ?? existing.stats?.high,
            low: incoming.stats?.low ?? existing.stats?.low,
            priceChangePercent:
              incoming.stats?.priceChangePercent ?? existing.stats?.priceChangePercent,
            volumeBase: incoming.stats?.volumeBase ?? existing.stats?.volumeBase,
            volumeQuote: incoming.stats?.volumeQuote ?? existing.stats?.volumeQuote,
          }
        : undefined,
    minPrice: incoming.minPrice ?? existing.minPrice,
    maxPrice: incoming.maxPrice ?? existing.maxPrice,
    estimatedDeliveryPrice: incoming.estimatedDeliveryPrice ?? existing.estimatedDeliveryPrice,
    fundingRate: incoming.fundingRate ?? existing.fundingRate,
    nextFundingTime: incoming.nextFundingTime ?? existing.nextFundingTime,
  }
}

export function useOptionChain(
  cardId: string,
  instruments: Instrument[],
  focusTicker: string | null | undefined,
  exchange: Exchange
) {
  const store = useStore()
  const {
    selectedOptionTicker: sharedSelectedTicker,
    setSelectedOptionTicker: setSharedSelectedTicker,
    selectedOptionExpiry: selectedExpiry,
    setSelectedOptionExpiry: setSelectedExpiry,
    focusedOptionStrike,
  } = useLinkingGroupState(cardId)
  const { optionRows: rows, setOptionRows: setRows } = useLinkingGroupOptionRows(cardId)
  const { setOptionUnderlyingPrice: setUnderlyingPrice } =
    useLinkingGroupOptionUnderlyingPrice(cardId)

  // `focusTicker` is the ticker family the widget currently rendering this table is
  // actually looking at (derived locally from its own active tab). It takes priority
  // over the linking-group's shared `selectedOptionTicker`, which can lag or get
  // overwritten by another widget in the same link color broadcasting a different
  // ticker in the same tick — see [[option-ticker-sync-loop]]. When no focusTicker is
  // given (e.g. a future non-tab-grouped consumer), fall back to the shared value.
  const selectedTicker = focusTicker ?? sharedSelectedTicker
  const setSelectedTicker = setSharedSelectedTicker

  // Keep the shared linking-group ticker in sync with this widget's authoritative
  // focus ticker so other widgets in the link group (positions, order form) follow it.
  useEffect(() => {
    if (focusTicker && focusTicker !== sharedSelectedTicker) {
      setSharedSelectedTicker(focusTicker)
    }
  }, [focusTicker, sharedSelectedTicker, setSharedSelectedTicker])
  const { setSelectedOptions } = useOrderFormDispatch(cardId)
  const { selectedOptions } = useOrderFormSelectionTarget(cardId)

  const prevTickerRef = useRef<string | null>(null)
  const prevExchangeRef = useRef<Exchange | null>(null)
  const prevAutoSelectTickerRef = useRef<string | null>(null)
  const hasAutoFixedExpiryRef = useRef(false)
  const hasCheckedStaleDataRef = useRef(false)

  useEffect(() => {
    if (prevExchangeRef.current === exchange) return
    prevExchangeRef.current = exchange
    setRows(new Map())
    setUnderlyingPrice(null)
  }, [exchange, setRows, setUnderlyingPrice])

  // Filter option instruments once, shared by grouping and symbol-lookup map
  const optionInstruments = useMemo(
    () => (instruments ?? []).filter((inst) => inst.kind === InstrumentKind.INSTRUMENT_KIND_OPTION),
    [instruments]
  )

  // Group option instruments by ticker
  const grouped = useMemo(() => filterAndGroupInstruments(optionInstruments), [optionInstruments])

  // symbol -> ParsedSymbol lookup, built from structured instrument fields
  const optionSymbolMap = useMemo(
    () => buildOptionSymbolMap(optionInstruments),
    [optionInstruments]
  )

  // Get available expiries for selected ticker
  const availableExpiries = useMemo(() => {
    if (!selectedTicker) return []
    const group = grouped.get(selectedTicker as string)
    if (!group) return []

    const now = Date.now()
    const allExpiries = [...group.expires.keys()]
    const expiries = allExpiries.filter((e) => {
      const tsMs = group.expiryTsMs.get(e)
      return isExpiryStillAvailable(e, tsMs)
    })
    return expiries.sort((a, b) => {
      const aTs = group.expiryTsMs.get(a) ?? getExpiryTimestamp(a)
      const bTs = group.expiryTsMs.get(b) ?? getExpiryTimestamp(b)
      return aTs - bTs
    })
  }, [grouped, selectedTicker])

  // Symbols to subscribe (for multi-stream)
  const subscriptionSymbols = useMemo(() => {
    if (!selectedTicker || !selectedExpiry) return []

    const group = grouped.get(selectedTicker as string)
    if (!group) return []

    return (group.expires.get(selectedExpiry) ?? []).map((entry) => entry.symbol)
  }, [selectedTicker, selectedExpiry, grouped])

  // Ref-count interest in the current expiry's option ticker cache entries so they're
  // evicted (after a grace period) once no expiry/ticker-tape is using them, instead of
  // accumulating forever across every underlying/expiry the user has ever viewed.
  useEffect(() => {
    for (const symbol of subscriptionSymbols) registerOptionTickerSymbol(symbol)
    return () => {
      for (const symbol of subscriptionSymbols) unregisterOptionTickerSymbol(symbol)
    }
  }, [subscriptionSymbols])

  // Apply a batch in one state update. The public handler below additionally merges
  // every WS delivery that arrives during the same animation frame, preventing a
  // burst from scheduling several React commits before the browser can paint once.
  const processTickers = useCallback(
    (tickers: IncrementalTicker[]) => {
      // Filter tickers to only process those matching the current selected ticker AND expiry.
      // Without the expiry check, late-arriving WS messages for the previous expiry would
      // insert stale strikes into rows after an expiry change.
      const relevantTickers =
        selectedTicker && selectedExpiry
          ? tickers.filter((t) => {
              const parsed = resolveOptionSymbol(t.symbol, optionSymbolMap)
              return parsed && parsed.ticker === selectedTicker && parsed.expiry === selectedExpiry
            })
          : []

      if (relevantTickers.length === 0) return

      // Write each processed ticker to its own per-symbol cache atom so other widgets
      // (e.g. ticker-tape) can read initial option data without waiting for their own
      // subscription snapshot — without cloning/rerendering off a single shared map.
      // Merged (not raw) even for UPDATE_TYPE_SNAPSHOT: a resubscribe/resync snapshot can
      // legitimately omit greeks (computed on a slower path than the rest of the ticker),
      // and replacing wholesale would flash delta/gamma/etc. back to 0 until the next
      // change message repopulates them — see mergeTickerUpdate's per-field greeks/stats
      // merge above.
      const mergedTickers = new Map<string, IncrementalTicker>()
      for (const ticker of relevantTickers) {
        const atomForSymbol = optionIncrementalTickerAtomFamily(ticker.symbol)
        const existing = store.get(atomForSymbol)
        const merged = mergeTickerUpdate(existing ?? undefined, ticker)
        store.set(atomForSymbol, merged)
        mergedTickers.set(ticker.symbol, merged)
      }

      // Read off the merged tickers (not the raw batch), so a partial update that omits the
      // price doesn't hide one already cached. OPRA/GLBX fall back to indexPrice — see
      // resolveChainUnderlyingPrice.
      const underlyingPrice = resolveChainUnderlyingPrice(mergedTickers.values(), exchange)
      if (underlyingPrice != null) setUnderlyingPrice(underlyingPrice)

      // Batch update all rows in a single setRows call
      setRows((prev) => {
        const next = new Map(prev)

        for (const ticker of relevantTickers) {
          const parsed = resolveOptionSymbol(ticker.symbol, optionSymbolMap)
          if (!parsed) continue

          const merged = mergedTickers.get(ticker.symbol) ?? ticker
          const existing = next.get(parsed.strike) || {}

          if (parsed.type === 'C') {
            next.set(parsed.strike, { ...existing, call: merged })
          } else {
            next.set(parsed.strike, { ...existing, put: merged })
          }
        }

        return next
      })
    },
    [selectedTicker, selectedExpiry, setUnderlyingPrice, setRows, store, optionSymbolMap, exchange]
  )

  const pendingTickersRef = useRef<IncrementalTicker[]>([])
  const tickerFrameRef = useRef<number | null>(null)
  const processTickersRef = useRef(processTickers)
  processTickersRef.current = processTickers

  const handleTickers = useCallback((tickers: IncrementalTicker[]) => {
    pendingTickersRef.current.push(...tickers)
    if (tickerFrameRef.current !== null) return

    tickerFrameRef.current = requestAnimationFrame(() => {
      tickerFrameRef.current = null
      const pending = pendingTickersRef.current
      pendingTickersRef.current = []
      processTickersRef.current(pending)
    })
  }, [])

  useEffect(
    () => () => {
      if (tickerFrameRef.current !== null) cancelAnimationFrame(tickerFrameRef.current)
      tickerFrameRef.current = null
      pendingTickersRef.current = []
    },
    []
  )

  // Auto-fix stale expiry: if the stored expiry is in the past, replace with nearest future expiry
  useEffect(() => {
    if (hasAutoFixedExpiryRef.current) return
    if (!selectedExpiry || availableExpiries.length === 0) return

    // If the stored expiry is not in the available (future) expiries, auto-correct to nearest future
    const isStale = selectedExpiry && !availableExpiries.includes(selectedExpiry)
    if (isStale) {
      const nearest = getNearesetExpiry(availableExpiries)
      if (nearest && nearest !== selectedExpiry) {
        const staleExpiry = selectedExpiry
        setSelectedExpiry(nearest)
        setRows(new Map())
        // Clear selectedOptions that reference the stale expiry
        setSelectedOptions((prev) =>
          prev.filter((opt) => {
            const parsed = resolveOptionSymbol(opt.instrumentName, optionSymbolMap)
            return !parsed || parsed.expiry !== staleExpiry
          })
        )
      }
    }

    hasAutoFixedExpiryRef.current = true
  }, [
    selectedExpiry,
    availableExpiries,
    setSelectedExpiry,
    setRows,
    setSelectedOptions,
    optionSymbolMap,
  ])

  // Check for stale data (rows belong to a different ticker) on initial mount
  useEffect(() => {
    if (hasCheckedStaleDataRef.current || !selectedTicker) return

    if (rows.size > 0) {
      const firstRowData = rows.values().next().value
      const callSymbol = firstRowData?.call?.symbol
      const putSymbol = firstRowData?.put?.symbol
      const symbol = callSymbol || putSymbol

      if (symbol && !symbol.startsWith(selectedTicker)) {
        setRows(new Map())
        setUnderlyingPrice(null)
      }
    }

    hasCheckedStaleDataRef.current = true
    // Note: We intentionally don't include rows in deps to avoid re-running on every rows change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTicker, setSelectedExpiry, setRows, setUnderlyingPrice])

  // Reset rows when ticker changes (e.g., BTC → ETH)
  // Note: selectedExpiry is intentionally NOT reset here. applyTicker() may have already set
  // the correct expiry for the new ticker (e.g. when navigating from a position click).
  // If the current expiry turns out to be invalid for the new ticker, the auto-select below
  // will correct it to the nearest available expiry.
  useEffect(() => {
    if (prevTickerRef.current !== null && prevTickerRef.current !== selectedTicker) {
      setRows(new Map())
      setUnderlyingPrice(null)
    }
    prevTickerRef.current = selectedTicker
  }, [selectedTicker, setRows, setUnderlyingPrice])

  // Auto-select ticker, expiry, and initialize table rows
  useEffect(() => {
    // Auto-select first ticker when no ticker is selected
    if (!selectedTicker && grouped.size > 0) {
      const firstTicker =
        PRIORITY_TICKERS.find((ticker) => grouped.has(ticker)) || [...grouped.keys()][0]
      if (firstTicker) {
        setSelectedTicker(firstTicker)
        return
      }
    }

    // Track ticker for cross-ticker expiry override detection.
    // Always update here (outside the availableExpiries guard) so the ref stays in sync
    // even when instruments haven't loaded yet.
    const prevTicker = prevAutoSelectTickerRef.current
    if (selectedTicker) prevAutoSelectTickerRef.current = selectedTicker

    // Auto-select nearest expiry when ticker is selected but has no expiry, or when the
    // current expiry is not available for the new ticker (e.g. after a cross-ticker navigation)
    if (selectedTicker && availableExpiries.length > 0) {
      // null prevTicker means this is the first run (initial mount), not a real ticker change.
      const tickerJustChanged = prevTicker !== null && prevTicker !== selectedTicker

      const selectedExpiryIsFuture = selectedExpiry
        ? isExpiryStillAvailable(
            selectedExpiry,
            grouped.get(selectedTicker)?.expiryTsMs.get(selectedExpiry)
          )
        : false

      // NOT gated on tickerJustChanged — a position/order-history row click can open the
      // option chain via a FRESH mount of this widget (chain wasn't visible before), in
      // which case prevAutoSelectTickerRef.current is still null and tickerJustChanged is
      // (correctly) false, even though focusedOptionStrike is exactly the contract that
      // was just clicked. Gating on tickerJustChanged there made that case fall through to
      // "nearest" instead of the clicked contract's own expiry.
      const matchesFocusedTicker = focusedOptionStrike?.ticker === selectedTicker

      const needsNewExpiry =
        !selectedExpiry ||
        !selectedExpiryIsFuture ||
        (tickerJustChanged && !availableExpiries.includes(selectedExpiry))

      if (needsNewExpiry) {
        const focusedExpiry =
          matchesFocusedTicker && focusedOptionStrike?.expiry ? focusedOptionStrike.expiry : null
        const nextExpiry =
          focusedExpiry && availableExpiries.includes(focusedExpiry)
            ? focusedExpiry
            : getNearesetExpiry(availableExpiries)

        if (nextExpiry && nextExpiry !== selectedExpiry) {
          setSelectedExpiry(nextExpiry)
          setRows(new Map())
          setUnderlyingPrice(null)
          return
        }
      }
    }

    // Initialize table rows when both ticker and expiry are selected
    if (selectedTicker && selectedExpiry) {
      const group = grouped.get(selectedTicker as string)
      if (!group) return

      const strikes = getStrikesForExpiry(group, selectedExpiry)

      setRows((prev) => {
        // If rows already have snapshot data for this ticker+expiry, don't flush.
        // This prevents multiple widgets sharing the same linking group from wiping
        // each other's snapshots on re-mount or re-render.
        if (prev.size > 0) {
          const firstWithData = [...prev.values()].find((v) => v.call || v.put)
          if (firstWithData) {
            const symbol = firstWithData.call?.symbol || firstWithData.put?.symbol
            const parsed = symbol ? resolveOptionSymbol(symbol, optionSymbolMap) : null
            if (parsed && parsed.ticker === selectedTicker && parsed.expiry === selectedExpiry) {
              return prev
            }
          }
        }

        const newRows = new Map<number, { call?: IncrementalTicker; put?: IncrementalTicker }>()
        strikes.forEach((strike) => newRows.set(strike, {}))
        return newRows
      })
    }
  }, [
    selectedTicker,
    selectedExpiry,
    grouped,
    availableExpiries,
    setSelectedTicker,
    setSelectedExpiry,
    setRows,
    setUnderlyingPrice,
    focusedOptionStrike, // needed so justChangedToFocusedTicker sees the latest focused strike
    optionSymbolMap,
  ])

  return {
    selectedTicker,
    selectedExpiry,
    rows,
    grouped,
    availableExpiries,
    setSelectedTicker,
    setSelectedExpiry,
    handleTickers,
    subscriptionSymbols,
    availableTickers: [...grouped.keys()],
    optionSymbolMap,
  }
}
